import { useEffect, useId, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/common/Button';
import { codiceInvito, leggiCodiceInvito } from '@/lib/gamehost/rest';
import { useAzioneTavolo, type GuastoAzione } from '@/lib/gamehost/useAzioneTavolo';
import { LIVELLI_ROBOT, POSTI_AMMESSI, POSTI_PREDEFINITI, eRobotNonDisponibili } from '@/lib/scala40/gioco';
import type { LivelloRobot } from '@/lib/scala40/gioco';
import { scala40Api } from '@/services/limuloScala40';

type Azione = 'gioca' | 'invita' | 'entra' | 'robot';
type Guasto = GuastoAzione | 'codiceNonValido' | 'robotNonDisponibili';

/** La frase di un guasto: una chiave di una lista chiusa sotto `hub.gioca.guasto.`. Il testo del server non si mostra MAI. */
function chiaveGuasto(azione: Azione, g: Guasto): string {
  switch (g) {
    case 'non-disponibile':
      return 'nonDisponibile';
    case 'conflitto':
      return azione === 'entra' ? 'conflittoEntra' : 'conflittoCrea';
    case 'nonTrovato':
      return azione === 'entra' ? 'nonTrovato' : 'errore';
    default:
      return g;
  }
}

/** Un'azione di scrittura per ogni formato (2..6): la chiave di idempotenza e PER CORPO, e `tipo` + `posti` sono corpi diversi. Cinque chiamate fisse, nell'ordine di `POSTI_AMMESSI`. */
function useAzioniPerPosti<R>() {
  const a2 = useAzioneTavolo<R>();
  const a3 = useAzioneTavolo<R>();
  const a4 = useAzioneTavolo<R>();
  const a5 = useAzioneTavolo<R>();
  const a6 = useAzioneTavolo<R>();
  return { 2: a2, 3: a3, 4: a4, 5: a5, 6: a6 } as Record<number, ReturnType<typeof useAzioneTavolo<R>>>;
}

/**
 * «GIOCA», «CONTRO I ROBOT», «INVITA UN AMICO» ed «ENTRA CON UN CODICE» nella Hub della scala 40 (sul modello di `GiocaGinRummy`, sul trasporto comune): le SCRITTURE, ognuna con la sua chiave di idempotenza
 * (`useAzioneTavolo`: il doppio tocco non crea due tavoli). Prima si sceglie il formato: da 2 a 6 giocatori (I-922, L-3454 b).
 *  · «Gioca»: un tavolo casual (`scala40.table.create`) per quel numero di posti, apre `/scala40/tavolo/:id`. Chi completa il tavolo lo decide il game host, non la TMA.
 *  · «Contro i robot»: un tavolo casual col campo `robot: { livello }` (L0 Principiante, L1 Intermedio, L2 Esperto), nella forma della briscola. Chi crea siede a un posto estratto dal core e OGNI altro posto
 *    (da 1 a 5) e un robot del pool dello stesso livello, col badge «bot». Senza abbastanza account nel pool il tavolo non nasce (503 `no_robot_available`, riconosciuto dallo status E dal codice): si dice in chiaro;
 *    un altro 503 resta un guasto di servizio. Il campo `robot` arriva al game host col BFF di Limulo (mirror qlash-limulo#252): la scala 40 e servita su Limulo staging dal 21/10 (I-1042). Mai classificato, mai privato: ranked e tornei sono P9, senza robot.
 *  · «Invita un amico»: un tavolo privato per quel numero di posti; mostra il CODICE da condividere e NON entra da solo (l'invito si vede una volta sola).
 *  · «Entra con un codice»: `scala40.table.join` col `tableId` e l'invito letti dal codice. Un codice che non ha la forma non parte nemmeno.
 * Il codice e una credenziale: sta SOLO nello stato del componente, mai in storage, in log o nell'URL. Nessun tavolo «in classifica»: ranked e tornei vengono dopo, solo a 2 posti. Il corpo del tavolo si
 * compone in UN punto, `creaTavoloScala40Body` (`lib/scala40/gioco.ts`).
 *
 * 🔴 Ogni azione ha una chiave di idempotenza PER CORPO: «Gioca a 3» e «Gioca a 4», e ognuno dei tre livelli dei robot per ogni formato, sono corpi diversi, e una chiave rimasta da un guasto incerto di uno riusata per l'altro prende 409
 * `idempotency_key_reused` dal core. Tornando all'azione di prima si ritrova la SUA chiave.
 */
export function GiocaScala40() {
  const { t } = useTranslation('scala40');
  const navigate = useNavigate();
  const titoloId = useId();
  const [crea] = scala40Api.useCreateScala40TableMutation();
  const [entra] = scala40Api.useJoinScala40TableMutation();
  const aGioca = useAzioniPerPosti<{ tableId: string }>();
  const aInvita = useAzioniPerPosti<{ tableId: string; codice: string | null }>();
  const aRobot = { L0: useAzioniPerPosti<{ tableId: string }>(), L1: useAzioniPerPosti<{ tableId: string }>(), L2: useAzioniPerPosti<{ tableId: string }>() };
  const aEntra = useAzioneTavolo<{ tableId: string }>();
  const [posti, setPosti] = useState<number>(POSTI_PREDEFINITI);
  const [invitoPronto, setInvitoPronto] = useState<{ tableId: string; codice: string | null } | null>(null);
  const [testo, setTesto] = useState('');
  const [codiceStorto, setCodiceStorto] = useState(false);
  const [ultima, setUltima] = useState<{ azione: Azione; posti: number; livello?: LivelloRobot } | null>(null);
  const [senzaRobot, setSenzaRobot] = useState(false);
  const occupato = [aGioca[posti], aInvita[posti], aEntra].some((a) => a?.stato.invio) || POSTI_AMMESSI.some((p) => aGioca[p]?.stato.invio || aInvita[p]?.stato.invio || LIVELLI_ROBOT.some((l) => aRobot[l][p]?.stato.invio));

  const vaiAlTavolo = (tableId: string) => navigate(`/scala40/tavolo/${tableId}`);

  const gioca = async () => {
    setUltima({ azione: 'gioca', posti });
    setInvitoPronto(null);
    const r = await aGioca[posti]!.esegui(async (idempotencyKey) => {
      const tavolo = await crea({ tipo: 'casual', posti, idempotencyKey }).unwrap();
      return tavolo === null ? null : { tableId: tavolo.tableId };
    });
    if (r !== null) vaiAlTavolo(r.tableId);
  };

  const controIlRobot = async (livello: LivelloRobot) => {
    setUltima({ azione: 'robot', posti, livello });
    setSenzaRobot(false);
    setInvitoPronto(null);
    const r = await aRobot[livello][posti]!.esegui(async (idempotencyKey) => {
      try {
        const tavolo = await crea({ tipo: 'casual', posti, idempotencyKey, livello }).unwrap();
        return tavolo === null ? null : { tableId: tavolo.tableId };
      } catch (e) {
        if (eRobotNonDisponibili(e)) setSenzaRobot(true);
        throw e;
      }
    });
    if (r !== null) vaiAlTavolo(r.tableId);
  };

  const invita = async () => {
    setUltima({ azione: 'invita', posti });
    setInvitoPronto(null);
    const r = await aInvita[posti]!.esegui(async (idempotencyKey) => {
      const tavolo = await crea({ tipo: 'privato', posti, idempotencyKey }).unwrap();
      return tavolo === null ? null : { tableId: tavolo.tableId, codice: tavolo.invito === null ? null : codiceInvito(tavolo.tableId, tavolo.invito) };
    });
    if (r !== null) setInvitoPronto(r);
  };

  const entraCol = async (e: FormEvent) => {
    e.preventDefault();
    setUltima({ azione: 'entra', posti });
    setInvitoPronto(null);
    const letto = leggiCodiceInvito(testo);
    setCodiceStorto(letto === null);
    if (letto === null) return; // niente richiesta per un codice che non ha la forma
    const r = await aEntra.esegui(async (idempotencyKey) => {
      const ingresso = await entra({ tableId: letto.tableId, invito: letto.invito, idempotencyKey }).unwrap();
      return ingresso === null ? null : { tableId: ingresso.tableId };
    });
    if (r !== null) {
      setTesto('');
      vaiAlTavolo(r.tableId);
    }
  };

  const guasto: Guasto | null =
    ultima === null
      ? null
      : ultima.azione === 'entra'
        ? codiceStorto
          ? 'codiceNonValido'
          : aEntra.stato.guasto
        : ultima.azione === 'gioca'
          ? (aGioca[ultima.posti]?.stato.guasto ?? null)
          : ultima.azione === 'robot'
            ? (() => {
                const g = aRobot[ultima.livello ?? 'L0'][ultima.posti]?.stato.guasto ?? null;
                return senzaRobot && g === 'server' ? 'robotNonDisponibili' : g;
              })()
            : (aInvita[ultima.posti]?.stato.guasto ?? null);

  return (
    <section data-testid="scala40-gioca" aria-labelledby={titoloId} className="flex shrink-0 flex-col gap-3">
      <h2 id={titoloId} className="text-xs font-bold uppercase tracking-wide text-fg-muted">
        {t('hub.gioca.titolo')}
      </h2>
      <p className="px-1 text-xs text-fg-muted">{t('hub.gioca.nota')}</p>
      <div role="group" aria-label={t('modo.titolo')} className="flex flex-col gap-1">
        <p className="px-1 text-sm font-semibold text-fg">{t('modo.titolo')}</p>
        <div className="grid grid-cols-5 gap-2">
          {POSTI_AMMESSI.map((n) => (
            <Button key={n} variant="scelta" forma="tessera" pressed={posti === n} data-posti={n} disabled={occupato} onClick={() => setPosti(n)}>
              {n}
            </Button>
          ))}
        </div>
        <p className="px-1 text-xs text-fg-muted">{posti === 2 ? t('modo.nota2') : t('modo.notaPiu', { n: posti })}</p>
      </div>
      <Button variant="primary" fullWidth data-azione="gioca" disabled={occupato} onClick={() => void gioca()}>
        {aGioca[posti]?.stato.invio ? t('hub.gioca.invio') : t('hub.gioca.gioca', { n: posti })}
      </Button>
      <div data-contro-robot className="flex flex-col gap-2">
        <p className="px-1 text-sm font-semibold text-fg">{t('hub.gioca.robot.titolo')}</p>
        <div className="grid grid-cols-3 gap-2">
          {LIVELLI_ROBOT.map((livello) => (
            <Button key={livello} variant="secondary" data-azione="robot" data-livello={livello} disabled={occupato} onClick={() => void controIlRobot(livello)}>
              {aRobot[livello][posti]?.stato.invio ? t('hub.gioca.invio') : t(`livello.${livello}`)}
            </Button>
          ))}
        </div>
        <p className="px-1 text-xs text-fg-muted">{posti === 2 ? t('hub.gioca.robot.nota2') : t('hub.gioca.robot.notaPiu', { n: posti - 1 })}</p>
      </div>
      <div className="flex flex-col gap-1">
        <Button variant="secondary" fullWidth data-azione="invita" disabled={occupato} onClick={() => void invita()}>
          {aInvita[posti]?.stato.invio ? t('hub.gioca.invio') : t('hub.gioca.invita')}
        </Button>
        <p className="px-1 text-xs text-fg-muted">{t('hub.gioca.invitaNota')}</p>
      </div>
      {invitoPronto !== null && <InvitoPronto pronto={invitoPronto} onVai={() => vaiAlTavolo(invitoPronto.tableId)} />}
      <form data-azione="entra" onSubmit={(e) => void entraCol(e)} className="flex flex-col gap-2">
        <label htmlFor={`${titoloId}-codice`} className="text-sm font-semibold text-fg">
          {t('hub.gioca.entra.titolo')}
        </label>
        <input
          id={`${titoloId}-codice`}
          type="text"
          value={testo}
          onChange={(e) => {
            setTesto(e.target.value);
            setCodiceStorto(false);
          }}
          placeholder={t('hub.gioca.entra.segnaposto')}
          autoCapitalize="none"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          disabled={occupato}
          className="min-h-11 w-full min-w-0 rounded-xl border border-border bg-bg-secondary px-3 text-sm text-fg placeholder:text-fg-muted"
        />
        <Button type="submit" variant="secondary" fullWidth disabled={occupato || testo.trim() === ''}>
          {aEntra.stato.invio ? t('hub.gioca.invio') : t('hub.gioca.entra.azione')}
        </Button>
      </form>
      {guasto !== null && (
        <p role="status" data-gioca-guasto={guasto} className="rounded-xl bg-bg-secondary px-4 py-3 text-sm text-danger">
          {t(`hub.gioca.guasto.${chiaveGuasto(ultima?.azione ?? 'gioca', guasto)}`)}
        </p>
      )}
    </section>
  );
}

/** Il codice da condividere, copiabile. «Copiato» si dice SOLO se la copia e riuscita; a copia fallita il codice si seleziona con un tocco lungo. */
function InvitoPronto({ pronto, onVai }: { pronto: { tableId: string; codice: string | null }; onVai: () => void }) {
  const { t } = useTranslation('scala40');
  const [esito, setEsito] = useState<'ok' | 'ko' | null>(null);
  useEffect(() => setEsito(null), [pronto.codice]);
  const copia = async () => {
    if (pronto.codice === null) return;
    try {
      await navigator.clipboard.writeText(pronto.codice);
      setEsito('ok');
    } catch {
      setEsito('ko');
    }
  };
  return (
    <div data-scala40-invito-pronto className="card-edge flex flex-col gap-2 rounded-xl bg-bg-secondary p-3">
      {pronto.codice === null ? (
        <p data-scala40-invito-assente className="text-sm text-fg-muted">
          {t('hub.gioca.codice.assente')}
        </p>
      ) : (
        <>
          <p className="text-sm font-semibold text-fg">{t('hub.gioca.codice.titolo')}</p>
          <code data-scala40-invito-codice className="select-all break-all rounded bg-bg-tertiary px-2 py-2 font-mono text-xs text-fg">
            {pronto.codice}
          </code>
          <p className="text-xs text-fg-muted">{t('hub.gioca.codice.avviso')}</p>
          <Button variant="secondary" fullWidth data-azione="copia" onClick={() => void copia()}>
            {esito === 'ok' ? t('hub.gioca.codice.copiato') : t('hub.gioca.codice.copia')}
          </Button>
          <p role="status" aria-live="polite" className={esito === 'ko' ? 'text-xs text-fg-muted' : 'sr-only'}>
            {esito === 'ko' ? t('hub.gioca.codice.copiaFallita') : esito === 'ok' ? t('hub.gioca.codice.copiato') : ''}
          </p>
        </>
      )}
      <Button variant="primary" fullWidth data-azione="vai" onClick={onVai}>
        {t('hub.gioca.codice.vai')}
      </Button>
    </div>
  );
}
